import { useToast } from '@chakra-ui/react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useApiClient } from '../../../apiClient';
import { translate } from '../../../i18n';
import { GET_EVENT_QUERY_KEY } from '../../event-management/hooks/useGetEvent';

interface KickEventParticipantResponse {
    success: boolean;
    message: string;
    refundAmount?: number;
    currency?: string;
}

interface KickEventParticipantRequest {
    eventId: number;
    participantUserId: string;
    reason?: string;
}

/**
 * Hook to kick/remove any participant from an event (admin functionality).
 * This endpoint allows admins to remove any participant with an optional reason.
 */
export const useKickEventParticipant = () => {
    const toast = useToast();
    const queryClient = useQueryClient();
    const apiClient = useApiClient();

    return useMutation({
        mutationFn: async ({ eventId, participantUserId, reason }: KickEventParticipantRequest) => {
            return apiClient.fetchApi<KickEventParticipantResponse, { reason?: string }>({
                endpoint: `events/${eventId}/kick-participant/${participantUserId}`,
                method: 'POST',
                body: { reason },
            });
        },
        onSuccess: (data, { eventId }) => {
            if (data.success) {
                toast({
                    title: translate('event.participant_removed_success'),
                    description: data.message,
                    status: 'success',
                    duration: 5000,
                    isClosable: true,
                });

                queryClient.invalidateQueries({ queryKey: GET_EVENT_QUERY_KEY(eventId) });
            } else {
                toast({
                    title: translate('event.participant_removed_error'),
                    description: data.message,
                    status: 'error',
                    duration: 5000,
                    isClosable: true,
                });
            }
        },
        onError: (error: any) => {
            toast({
                title: translate('event.participant_removed_error'),
                description: error?.message || translate('event.participant_removed_unexpected'),
                status: 'error',
                duration: 5000,
                isClosable: true,
            });
        },
    });
};
